import { type ReactNode, useEffect } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { Text } from '@/components/ui';
import { palette, useTheme } from '@/theme';

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg';
}) {
  const theme = useTheme();

  useEffect(() => {
    if (!open || Platform.OS !== 'web' || typeof document === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <View
      style={[
        styles.backdrop,
        Platform.OS === 'web' ? ({ position: 'fixed' } as any) : null,
      ]}
    >
      <Pressable accessibilityLabel="Close" onPress={onClose} style={styles.scrim} />
      <View
        style={[
          styles.card,
          {
            backgroundColor: theme.colors.bg,
            borderRadius: theme.radii.md,
            maxWidth: size === 'lg' ? 960 : 720,
            borderColor: theme.colors.rule,
          },
        ]}
      >
        <View style={[styles.head, { borderBottomColor: theme.colors.rule }]}>
          <View style={{ flex: 1 }}>
            <Text variant="h3">{title}</Text>
            {subtitle ? (
              <Text variant="meta" tone="fgMute" style={{ marginTop: 4 }}>
                {subtitle}
              </Text>
            ) : null}
          </View>
          <Pressable
            accessibilityRole="button"
            onPress={onClose}
            style={[
              styles.closeBtn,
              { borderColor: theme.colors.rule, borderRadius: theme.radii.pill },
            ]}
          >
            <Text variant="meta">esc</Text>
          </Pressable>
        </View>
        <ScrollView contentContainerStyle={styles.body}>{children}</ScrollView>
        {footer ? (
          <View style={[styles.foot, { borderTopColor: theme.colors.rule }]}>{footer}</View>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    zIndex: 100,
  },
  scrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: palette.ink,
    opacity: 0.5,
  },
  card: {
    width: '100%',
    maxHeight: '92%',
    borderWidth: 1,
    overflow: 'hidden',
  },
  head: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
    padding: 24,
    borderBottomWidth: 1,
  },
  closeBtn: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderWidth: 1,
  },
  body: {
    padding: 24,
  },
  foot: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    padding: 24,
    borderTopWidth: 1,
  },
});
